import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import PublicLayout from '../components/PublicLayout';
import { api, useAuth } from '../App';
import { ArrowLeft, MapPin, Calendar, IndianRupee, Users, Heart, Send, CheckCircle, XCircle, Star, Download, FileText, ArrowRight } from 'lucide-react';

export default function BriefDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interests, setInterests] = useState([]);
  const [loadingInterests, setLoadingInterests] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const isOwner = user?.role === 'client' && brief?.client_id === user?.user_id;
  const isResearcher = user?.role === 'researcher';
  
  useEffect(() => {
    const fetchBrief = async () => {
      try {
        const response = await api.get(`/briefs/${id}`);
        setBrief(response.data);
        
        if (user?.role === 'client') {
          setLoadingInterests(true);
          try {
            const intRes = await api.get(`/briefs/${id}/interests`);
            setInterests(intRes.data || []);
          } catch (e) {}
          setLoadingInterests(false);
        }
      } catch (error) {
        console.error('Error fetching brief:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBrief();
  }, [id, user]);
  
  const expressInterest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/briefs/${id}/interest`, {
        brief_id: id,
        message: message
      });
      setBrief(prev => ({ ...prev, has_expressed_interest: true }));
      setMessage('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to express interest');
    } finally {
      setSubmitting(false);
    }
  };
  
  const withdrawInterest = async () => {
    try {
      await api.delete(`/briefs/${id}/interest`);
      setBrief(prev => ({ ...prev, has_expressed_interest: false }));
    } catch (error) {
      console.error('Error withdrawing interest:', error);
    }
  };
  
  const updateInterestStatus = async (interestId, status) => {
    try {
      await api.put(`/briefs/interests/${interestId}/status?status=${status}`);
      setInterests(prev => prev.map(i => i.interest_id === interestId ? { ...i, status } : i));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };
  
  const formatBudget = (min, max) => {
    if (!min && !max) return null;
    const formatNum = (n) => n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : `₹${(n/1000).toFixed(0)}K`;
    if (min && max) return `${formatNum(min)} - ${formatNum(max)}`;
    if (max) return `Up to ${formatNum(max)}`;
    return `From ${formatNum(min)}`;
  };
  
  const Layout = user ? DashboardLayout : PublicLayout;
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-800"></div>
        </div>
      </Layout>
    );
  }
  
  if (!brief) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-neutral-500">Brief not found</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-sm text-neutral-600 hover:underline">Go back</button>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-6"
      >
        <ArrowLeft size={20} />
        Back
      </button>
      
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white border border-neutral-200 p-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-bold px-2 py-0.5 bg-purple-100 text-purple-700">BRIEF</span>
              <span className={`text-xs px-2 py-0.5 ${brief.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-600'}`}>
                {brief.status}
              </span>
            </div>
            
            <h1 className="text-2xl lg:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
              {brief.title}
            </h1>
            <p className="text-neutral-500 mb-6">{brief.company_name || brief.client?.name}</p>
            
            <div className="flex flex-wrap gap-4 text-sm text-neutral-600 mb-6 pb-6 border-b border-neutral-200">
              {brief.location && (
                <span className="flex items-center gap-2"><MapPin size={16} />{brief.location}</span>
              )}
              {brief.timeline && (
                <span className="flex items-center gap-2"><Calendar size={16} />{brief.timeline}</span>
              )}
              {formatBudget(brief.budget_min, brief.budget_max) && (
                <span className="flex items-center gap-2"><IndianRupee size={16} />{formatBudget(brief.budget_min, brief.budget_max)}</span>
              )}
              <span className="flex items-center gap-2"><Users size={16} />{brief.interest_count} interested</span>
            </div>
            
            <div className="mb-6">
              <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>Description</h2>
              <p className="text-neutral-700 whitespace-pre-wrap">{brief.description}</p>
            </div>
            
            {brief.objectives && (
              <div className="mb-6">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>Objectives</h2>
                <p className="text-neutral-700 whitespace-pre-wrap">{brief.objectives}</p>
              </div>
            )}
            
            {brief.target_audience && (
              <div className="mb-6">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>Target Audience</h2>
                <p className="text-neutral-700">{brief.target_audience}</p>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              {brief.method && <span className="px-3 py-1.5 bg-neutral-100 text-neutral-700 text-sm">{brief.method}</span>}
              {brief.sector && <span className="px-3 py-1.5 bg-neutral-100 text-neutral-700 text-sm">{brief.sector}</span>}
              {brief.languages_required?.map((lang, i) => (
                <span key={i} className="px-3 py-1.5 bg-neutral-100 text-neutral-700 text-sm">{lang}</span>
              ))}
            </div>
            
            {brief.attachment_url && (
              <div className="mt-6 pt-6 border-t border-neutral-200">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  <FileText size={18} className="inline mr-2" />
                  Brief Document
                </h2>
                <a
                  href={brief.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-brief-doc"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0D7377] text-white hover:bg-[#0A5C5F] transition-colors text-sm font-medium"
                >
                  <Download size={16} />
                  Download Brief Document
                </a>
              </div>
            )}
          </div>
          
          {/* Interests (for owner) */}
          {isOwner && (
            <div className="bg-white border border-neutral-200 p-8 mt-6">
              <h2 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
                Interested Researchers ({interests.length})
              </h2>
              
              {loadingInterests ? (
                <div className="py-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0D7377] mx-auto"></div>
                </div>
              ) : interests.length === 0 ? (
                <p className="text-neutral-500 py-4">No interest expressed yet</p>
              ) : (
                <div className="space-y-4">
                  {interests.map((interest) => (
                    <div key={interest.interest_id} className="border border-neutral-200 p-4">
                      <div className="flex items-start gap-4">
                        {interest.researcher?.picture ? (
                          <img src={interest.researcher.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-[#E6F3F4] flex items-center justify-center text-[#0D7377] font-bold">
                            {interest.researcher?.name?.[0]?.toUpperCase()}
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <Link to={`/profile/${interest.researcher_id}`} className="font-semibold hover:text-[#0D7377]">
                              {interest.researcher?.name}
                            </Link>
                            <span className={`text-xs px-2 py-1 rounded-sm font-medium ${
                              interest.status === 'shortlisted' ? 'bg-yellow-100 text-yellow-700' :
                              interest.status === 'selected' ? 'bg-green-100 text-green-700' :
                              interest.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-neutral-100 text-neutral-600'
                            }`}>
                              {interest.status}
                            </span>
                          </div>
                          
                          {interest.profile?.headline && (
                            <p className="text-sm text-neutral-500">{interest.profile.headline}</p>
                          )}
                          
                          {interest.message && (
                            <p className="text-sm text-neutral-600 mt-2 bg-neutral-50 p-3">{interest.message}</p>
                          )}
                          
                          {interest.status === 'interested' && (
                            <div className="flex gap-2 mt-3">
                              <button
                                onClick={() => updateInterestStatus(interest.interest_id, 'shortlisted')}
                                className="px-3 py-1.5 bg-yellow-100 text-yellow-700 text-sm flex items-center gap-1"
                              >
                                <Star size={14} /> Shortlist
                              </button>
                              <button
                                onClick={() => updateInterestStatus(interest.interest_id, 'selected')}
                                className="btn-client px-3 py-1.5 text-sm"
                              >
                                Select
                              </button>
                              <button
                                onClick={() => updateInterestStatus(interest.interest_id, 'rejected')}
                                className="px-3 py-1.5 border border-neutral-200 text-sm hover:bg-neutral-50"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                          
                          {interest.status === 'shortlisted' && (
                            <div className="flex gap-2 mt-3">
                              <button
                                onClick={() => updateInterestStatus(interest.interest_id, 'selected')}
                                className="btn-client px-3 py-1.5 text-sm"
                              >
                                Select
                              </button>
                              <button
                                onClick={() => updateInterestStatus(interest.interest_id, 'rejected')}
                                className="px-3 py-1.5 border border-neutral-200 text-sm hover:bg-neutral-50"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Sidebar */}
        <div className="lg:col-span-1">
          {isResearcher && (
            <div className="bg-white border border-neutral-200 p-6 mb-6">
              {brief.has_expressed_interest ? (
                <div className="text-center">
                  <CheckCircle className="text-green-500 mx-auto mb-2" size={32} />
                  <p className="font-semibold text-green-700">Interest Expressed</p>
                  <p className="text-sm text-neutral-500 mt-1">The client will review your profile</p>
                  <button
                    onClick={withdrawInterest}
                    className="mt-4 text-sm text-red-500 hover:underline"
                  >
                    Withdraw Interest
                  </button>
                </div>
              ) : brief.status !== 'open' ? (
                <div className="text-center">
                  <XCircle className="text-neutral-400 mx-auto mb-2" size={32} />
                  <p className="font-semibold text-neutral-600">Brief Closed</p>
                </div>
              ) : (
                <form onSubmit={expressInterest}>
                  <h3 className="font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>Express Interest</h3>
                  <textarea
                    placeholder="Write a short message (optional)..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-neutral-200 mb-3 resize-none"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-researcher w-full py-3 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Heart size={18} />
                        I'm Interested
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          )}
          
          {/* Public CTA */}
          {!user && (
            <div className="bg-white border border-neutral-200 p-6 mb-6" data-testid="public-cta-card">
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
                Interested in this brief?
              </h3>
              <p className="text-sm text-neutral-500 mb-4">
                Sign up to express interest, download documents, and connect with clients.
              </p>
              <button onClick={() => login('researcher')} data-testid="public-cta-join" className="btn-researcher w-full py-3 flex items-center justify-center gap-2 mb-2">
                Join as Researcher <ArrowRight size={16} />
              </button>
              <button onClick={() => login('client')} className="w-full py-2.5 text-sm text-[#0D7377] border border-[#0D7377] hover:bg-[#E6F3F4] flex items-center justify-center gap-2">
                Join as Client <ArrowRight size={16} />
              </button>
            </div>
          )}
          
          <div className="bg-white border border-neutral-200 p-6">
            <h3 className="font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>About the Client</h3>
            <div className="flex items-center gap-3">
              {brief.client?.picture ? (
                <img src={brief.client.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#E6F3F4] flex items-center justify-center text-[#0D7377] font-bold text-lg">
                  {brief.client?.name?.[0]?.toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-semibold">{brief.client?.name}</p>
                <span className="badge-client text-xs">Client</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
